import { Check, AlertCircle, Loader2, Shield, Merge, UserPlus, Smartphone } from 'lucide-react';

import type { SystemCheckResult } from '../../../../stores/registerStore';

/**
 * Step 3: 系统检测（分支注册向导）
 * 分支：已注册 → 登录引导 | 多身份 → 合并 | 客户档案 → 绑定 |
 *       老号回收 → 短信释放（releaseToken）| 升格换号 → 新手机短信持有权 | 新用户 → 直达密码步
 */
export function Step3SystemCheck({
  loading,
  result,
  onIdentityMerge,
  onCustomerBinding,
  onLogin,
  releaseFlow,
  releaseCountdown,
  releaseSending,
  onReleaseCodeChange,
  onSendReleaseCode,
  onVerifyReleaseCode,
  maskedPhone,
  smsCodeCountdown,
  smsCodeSending,
  smsCode,
  onSmsCodeChange,
  onSendUpgradeSmsCode,
  smsCodeError,
}: {
  loading: boolean;
  result: SystemCheckResult | null;
  onIdentityMerge: () => void;
  onCustomerBinding: () => void;
  onLogin: () => void;
  releaseFlow: {
    code: string;
    token: string | null;
    verifying: boolean;
  };
  releaseCountdown: number;
  releaseSending: boolean;
  onReleaseCodeChange: (code: string) => void;
  onSendReleaseCode: () => void;
  onVerifyReleaseCode: () => void;
  maskedPhone: string;
  smsCodeCountdown: number;
  smsCodeSending: boolean;
  smsCode: string;
  onSmsCodeChange: (code: string) => void;
  onSendUpgradeSmsCode: () => void;
  smsCodeError?: string;
}) {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4" />
        <p className="text-lg font-medium text-slate-700">系统正在检测您的信息...</p>
        <p className="text-sm text-slate-500 mt-2">请稍候，这可能需要几秒钟时间</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin mb-4" />
        <p className="text-lg font-medium text-slate-700">正在初始化检测...</p>
      </div>
    );
  }

  // 人工复核（由主控制器弹窗处理，此处兜底展示）
  if (result.requiresReview) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-amber-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3">需要人工复核</h4>
        <p className="text-slate-600 mb-2">您的注册信息与系统内记录存在冲突</p>
        <p className="text-slate-500 text-sm">请通过人工审核提交核对申请，客服将在 1-3 个工作日内处理</p>
      </div>
    );
  }

  // 已注册用户
  if (result.exists && result.isRegistered) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-10 h-10 text-amber-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3">您已注册</h4>
        <p className="text-slate-600 mb-2">您的信息已在系统中注册</p>
        <p className="text-slate-500 text-sm mb-6">请直接登录，或使用找回密码功能</p>
        <div className="flex gap-4">
          <button
            onClick={onLogin}
            className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            立即登录
          </button>
        </div>
      </div>
    );
  }

  // 多重身份
  if (result.multipleIdentities) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Merge className="w-10 h-10 text-orange-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3">检测到多重身份</h4>
        <p className="text-slate-600 mb-2">系统检测到您的个人信息在系统内存在多个身份</p>
        <p className="text-slate-500 text-sm mb-6">需要进行身份合并以确保您在系统内身份的唯一性</p>
        <button
          onClick={onIdentityMerge}
          className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          进行身份合并
        </button>
      </div>
    );
  }

  // 升格换号：主手机与档案不一致 → 需短信验证新手机持有权（防接管）。
  // 必须优先于客户档案认领分支：phoneChanged 时旧档案手机号用户已无法接收短信，
  // 若先弹认领引导将陷入「认领失败→重新检测→再弹认领」死循环；
  // 验证新号码持有权后，换号验证码随注册提交由后端校验并更新档案手机号。
  if (result.phoneChanged) {
    const canSend = smsCodeCountdown <= 0 && !smsCodeSending;
    return (
      <div className="py-4">
        <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Smartphone className="w-10 h-10 text-blue-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3 text-center">验证新手机号</h4>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-800 leading-relaxed">
          <p>
            检测到您填写的手机号 <span className="font-medium">{maskedPhone}</span> 与系统内您的历史档案登记号码不一致。
            为保护账号安全，请通过短信验证证明该号码当前归您使用，验证通过后注册信息将同步更新。
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="请输入 6 位短信验证码"
              value={smsCode}
              onChange={(e) => onSmsCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm tracking-widest text-center font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={onSendUpgradeSmsCode}
              disabled={!canSend}
              className="px-4 py-3 border border-teal-600 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-50 disabled:border-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {smsCodeSending
                ? '发送中...'
                : smsCodeCountdown > 0
                ? `${smsCodeCountdown}s 后重发`
                : '获取验证码'}
            </button>
          </div>
          {smsCodeError && <p className="text-xs text-red-500 text-center">{smsCodeError}</p>}
          <p className="text-xs text-slate-400 text-center">验证码有效期 5 分钟</p>
        </div>
      </div>
    );
  }

  // 客户档案命中（认领流程）：显示认领引导；认领/申诉/人工申请弹窗关闭后回到此页，
  // 不得回落到「检测通过」兜底（违反系统内身份唯一原则）
  // phoneOnlyMatch：仅手机号命中（档案登记姓名与填写不符），同样进入认领确认页人工比对
  if (result.matchedEntity || result.matchedEntities?.length) {
    const phoneOnly = result.phoneOnlyMatch === true;
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <UserPlus className="w-10 h-10 text-teal-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3">检测到您的客户档案</h4>
        <p className="text-slate-600 mb-2">
          {phoneOnly
            ? '系统中该手机号已关联客户档案（登记姓名与您填写的不一致，可能登记时使用了称谓）'
            : '系统中已存在与您匹配的客户档案（可能由伙伴或商家代为创建）'}
        </p>
        <p className="text-slate-500 text-sm mb-6">请结合档案照片与信息核对是否为本人，并确认是否认领</p>
        <button
          onClick={onCustomerBinding}
          className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          比对并确认档案归属
        </button>
      </div>
    );
  }


  // 新用户：releaseRequired 要求先过短信验证；其余为普通新用户
  if (result.releaseRequired) {
    const hintName = result.phoneConflictHint?.oldOwnerNameMasked as string | undefined;
    const verified = !!releaseFlow.token;
    const canResend = releaseCountdown <= 0 && !releaseSending && !verified;
    return (
      <div className="py-4">
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Shield className="w-10 h-10 text-amber-500" />
        </div>
        <h4 className="text-2xl font-bold text-slate-800 mb-3 text-center">
          需要验证手机号归属
        </h4>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 text-sm text-amber-800 leading-relaxed">
          <p className="mb-1">
            您填写的手机号 <span className="font-medium">{maskedPhone}</span>{' '}
            在系统内已由{' '}
            <span className="font-medium">{hintName || '其他联系人'}</span>{' '}
            登记。
          </p>
          <p>为保护原持有人权益，请先通过短信验证证明该号码当前归您使用，验证通过后旧登记将被自动释放。</p>
        </div>

        {verified ? (
          <div className="flex items-center justify-center gap-2 py-4 px-5 bg-green-50 border border-green-200 rounded-lg">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">手机号验证成功，请点击下方“继续注册”</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="请输入 6 位短信验证码"
                value={releaseFlow.code}
                onChange={(e) => onReleaseCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-lg text-sm tracking-widest text-center font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                disabled={releaseFlow.verifying}
              />
              <button
                type="button"
                onClick={onSendReleaseCode}
                disabled={!canResend}
                className="px-4 py-3 border border-teal-600 text-teal-600 rounded-lg text-sm font-medium hover:bg-teal-50 disabled:border-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                {releaseSending
                  ? '发送中...'
                  : releaseCountdown > 0
                  ? `${releaseCountdown}s 后重发`
                  : '获取验证码'}
              </button>
            </div>
            <button
              type="button"
              onClick={onVerifyReleaseCode}
              disabled={releaseFlow.verifying || releaseFlow.code.length !== 6}
              className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
            >
              {releaseFlow.verifying ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  验证中...
                </span>
              ) : (
                '验证手机号'
              )}
            </button>
            <p className="text-xs text-slate-400 text-center">
              验证码有效期 5 分钟。若手机号已不是您的，请更换手机号后重新填写。
            </p>
          </div>
        )}
      </div>
    );
  }

  // 新用户（可能包含老手机号回收提示 phoneRecycled，仅提示不阻塞）
  return (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-green-500" />
      </div>
      <h4 className="text-2xl font-bold text-slate-800 mb-3">检测通过</h4>
      {result.phoneRecycled ? (
        <p className="text-slate-600 mb-2">您的手机号曾被其他联系人登记（不影响注册，旧登记将自动释放）</p>
      ) : (
        <p className="text-slate-600 mb-2">系统中未查询到您的信息</p>
      )}
      <p className="text-slate-500 text-sm mb-6">您将作为新用户注册</p>
    </div>
  );
}